import _ from "lodash";
import {
    CumulativeDate,
    FdmAttribute,
    IGridEntry,
    IGridEntryPricing,
    LineItemAttributes,
    type IOrderCurrency,
} from "types/cart/shipment-review";
import { IWorkingOrderLineItemSizePricing } from "types/cart/pricing";
import { IInventoryMap } from "types/inventory";
import { SchemaField } from "types/schema";
import { isAllocated, isAllocatedByDoor } from "helpers/legacy-cart/bulk";
import { ILineItem } from "constants/order-types";
import { applyVisibilityRules, getAvailableInventory, isOverordered } from "helpers/inventory";
import { getStartOfDate } from "helpers/dates";
import { getLineItemDate } from "components/shipment-review/helpers/inventory";
import { isFDMSupports, mapLineItemFDMField } from "containers/legacy-cart/helpers/common";
import type { IProduct, IProductSize } from "types/product";
import type { ILegacyInventory } from "reducers/cart-grid-view";
import type { IScope } from "reducers/scope";
import type { Inventory } from "components/shipment-review/types";

export const lineItemKeyRegExp = /^line_items\./i;

export const generateGridEntryId = ({
    lineItemId,
    sizeId,
    size,
    location,
}: {
    lineItemId: string;
    sizeId: string;
    size: string;
    location: string;
}) => generateLineItemId(lineItemId, sizeId || generatePlaceholderLineItemId({ size, location }));

const generateLineItemId = (lineItemId: string, lineItemSizeId?: string, location?: string) =>
    [lineItemId, lineItemSizeId, location].filter((id) => !!id).join("_");

const generatePlaceholderLineItemId = ({ size, location }: { size: string; location?: string }) =>
    generateLineItemId("placeholderId", size, location);

export const getEntryPricing = ({
    sizePricing,
    currency,
    discount,
}: {
    sizePricing: IWorkingOrderLineItemSizePricing;
    currency: IOrderCurrency;
    discount: number;
}) => ({
    currency,
    discount,
    ...getSizePricesWithRounding(sizePricing, currency),
});

const getSizePricesWithRounding = (
    sizePricing: IWorkingOrderLineItemSizePricing,
    currency: IOrderCurrency,
): Omit<IGridEntryPricing, "currency" | "discount"> => {
    const wholesale = _.round(
        sizePricing?.discounted_price ?? sizePricing?.wholesale,
        currency.wholesale.decimalPlaces,
    );
    const original_wholesale = _.round(sizePricing?.original_price, currency.wholesale.decimalPlaces);
    const adjusted_wholesale = _.isNil(sizePricing?.adjusted_wholesale)
        ? undefined
        : _.round(sizePricing?.adjusted_wholesale, currency.wholesale.decimalPlaces);
    const discounted_price = _.isNil(sizePricing?.discounted_price)
        ? undefined
        : _.round(sizePricing?.discounted_price, currency.wholesale.decimalPlaces);
    const retail = _.round(sizePricing?.retail, currency.retail.decimalPlaces);
    return {
        wholesale: _.isNaN(wholesale) ? 0 : wholesale,
        original_wholesale: _.isNaN(original_wholesale) ? wholesale : original_wholesale,
        adjusted_wholesale,
        discounted_price,
        wholesale_range: _.map(sizePricing?.prices_range, (price) =>
            _.round(price, currency.wholesale.decimalPlaces),
        ) as [number, number],
        original_wholesale_range: _.map(sizePricing?.original_prices_range, (price) =>
            _.round(price, currency.wholesale.decimalPlaces),
        ) as [number, number],
        retail: _.isNaN(retail) ? 0 : retail,
    };
};

export const getEntryAttributes = (params: {
    rawLineItem: ILineItem;
    lineItemSchemaFields: SchemaField[];
    product: IProduct;
    checkErrors: boolean;
}): LineItemAttributes =>
    _.reduce(
        params.lineItemSchemaFields,
        (acc, schemaField) => {
            // Lineitem's schema is prefixed by `line_items.`
            const field = mapLineItemFDMField(schemaField);
            const value = params.rawLineItem[field] ?? getDefaultAttributeValue(schemaField);
            const lookup = schemaField.details.lookup;

            const isProductLookup = lookup?.startsWith("product.");
            const lookupValue = isProductLookup ? params.product[_.last(lookup.split("product."))] : null;

            const hasError: boolean =
                params.checkErrors &&
                schemaField.required &&
                _.isEmpty(value) &&
                isFDMSupports(schemaField) &&
                (!isProductLookup || _.isArray(lookupValue) || !_.isEmpty(lookupValue));

            acc[field] = {
                field: field,
                type: schemaField.details.subtype,
                lookup,
                lookupValue,
                value,
                hasError,
            } as FdmAttribute;
            return acc;
        },
        {},
    );

export const getDefaultAttributeValue = (field: SchemaField) =>
    field?.details.type === "multi" ? [] : field?.details.subtype === "date" ? null : "";

export const getAllocatedEntries = (entries: IGridEntry[]): IGridEntry[] =>
    _.filter(entries, (entry) => isAllocated(entry.allocated, entry.location));

export const getEntryInventoryData =
    (availability: ILegacyInventory, replenishments: IInventoryMap, brand: IScope["brand"]) =>
    (warehouseId: string, productId: string, sku: string, date: number): Inventory => {
        if (replenishments?.[warehouseId]?.[sku]) {
            const available = getAvailableInventory(date, replenishments?.[warehouseId]?.[sku]);
            return { available, displayQuantity: applyVisibilityRules(available, brand) };
        }

        const inventory = availability?.[productId]?.[sku]?.inventory?.[`${warehouseId}_${date}`];
        if (!_.isEmpty(inventory)) {
            return { available: inventory.quantity, displayQuantity: applyVisibilityRules(inventory.quantity, brand) };
        }

        return { available: null, displayQuantity: null };
    };

export const getOverorderedSkus = (
    replenishments: IInventoryMap,
    lineItems: ILineItem[],
    productsMap: { [productId: string]: IProduct },
    cumulativeDate: CumulativeDate,
): Set<string> => {
    const requestsMap: { [key: string]: { quantity: number; date: number; warehouse: string; skuId: string }[] } = {};
    _.forEach(lineItems, (lineItem) => {
        const product = productsMap[lineItem.product];
        if (lineItem.prebook || !product?.__tracks_inventory) {
            return;
        }

        lineItem.sizes.forEach((size) => {
            const warehouse = size.warehouse || lineItem.warehouse || null;
            const sku = _.find(product.sizes, { size: size.size })?._id;
            if (
                size.quantity &&
                isAllocatedByDoor(lineItem.allocated, size.location) &&
                replenishments?.[warehouse]?.[sku]
            ) {
                const date = getStartOfDate(getLineItemDate(lineItem, cumulativeDate));
                const key = `${warehouse}_${sku}`;
                requestsMap[key] ??= [];
                requestsMap[key].push({ quantity: size.quantity, date, warehouse, skuId: sku });
            }
        });
    });

    const set = new Set<string>();
    _.forEach(requestsMap, (requests) => {
        _.forEach(requests, (pivot) => {
            if (isOverordered(pivot, requests, replenishments?.[pivot.warehouse]?.[pivot.skuId])) {
                set.add(`${pivot.warehouse}_${pivot.skuId}_${pivot.date}`);
            }
        });
    });
    return set;
};

// size in product stored as `${sizeName}${size_group}`
export const getSizeName = (size: IProductSize) =>
    size.size.replace(new RegExp(`${_.escapeRegExp(size.size_group)}$`, "i"), "");

export const getSizeLabel = (size: IProductSize) =>
    size.size_group ? `${size.size_group} - ${getSizeName(size)}` : size.size;
